/**
 * 하단 이벤트 로그: 시간·레벨·노드명·메시지, 레벨 필터, 자동 스크롤, 지우기, 높이 3단계·접기.
 * 최대 500건(store 상한)을 가상화 없이 렌더하되 행은 memo.
 */
import { ChevronDown, ChevronUp, Rows2, Rows3, Rows4, Trash2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatLocalTime, formatTimeTooltip } from "../../lib/format";
import { filterEvents, selectActions, selectEvents, selectFilters, selectNodes, selectUiState, usePipelineStore, } from "../../store/pipeline-store";
import { Chip, IconButton, LEVEL_COLOR_VAR, LEVEL_LABEL_KO, LEVEL_SHORT, LevelBadge, cn } from "./ui";
const LEVELS = ["info", "success", "warning", "error"];
const HEIGHT_PX = { s: 140, m: 220, l: 340 };
const HEIGHT_LABEL = { s: "작게", m: "보통", l: "크게" };
const NEXT_HEIGHT = { s: "m", m: "l", l: "s" };
const HEIGHT_ICON = { s: Rows2, m: Rows3, l: Rows4 };
const EventRow = memo(function EventRow({ e, nodeLabel, onPick, }) {
    const clickable = !!e.nodeId;
    return (<li className={cn("flex min-w-0 items-center gap-2 px-3 py-[3px] text-[11px] leading-snug", clickable && "cursor-pointer hover:bg-surface-hover", e.level === "error" && "bg-status-error/5")} onClick={clickable ? () => onPick(e.nodeId) : undefined} onKeyDown={clickable
            ? (ev) => {
                if (ev.key === "Enter")
                    onPick(e.nodeId);
            }
            : undefined} role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined} title={clickable ? `${nodeLabel ?? e.nodeId} 선택 · ${formatTimeTooltip(e.timestamp)}` : formatTimeTooltip(e.timestamp)}>
      <span className="tabular w-[62px] shrink-0 text-fg-subtle">{formatLocalTime(e.timestamp)}</span>
      <LevelBadge level={e.level}/>
      <span className="w-[130px] shrink-0 truncate text-fg-muted" title={nodeLabel ?? e.nodeId ?? "시스템"}>
        {nodeLabel ?? e.nodeId ?? "시스템"}
      </span>
      <span className="min-w-0 flex-1 truncate text-fg" title={e.message}>
        {e.message}
      </span>
    </li>);
});
export const EventLogPanel = memo(function EventLogPanel({ onNavigate, className }) {
    const events = usePipelineStore(selectEvents);
    const filters = usePipelineStore(selectFilters);
    const nodes = usePipelineStore(selectNodes);
    const ui = usePipelineStore(selectUiState);
    const { setFilters, setUiState, clearEvents, requestFocusNode } = usePipelineStore(selectActions);
    const [height, setHeight] = useState("m");
    const listRef = useRef(null);
    const collapsed = ui.eventLogCollapsed;
    const labelById = useMemo(() => new Map(nodes.map((n) => [n.id, n.data.label])), [nodes]);
    // store 는 최신순 → 화면은 오래된 것이 위, 최신이 아래 (자동 스크롤은 맨 아래로)
    const visible = useMemo(() => filterEvents(events, filters).slice().reverse(), [events, filters]);
    const levelCounts = useMemo(() => {
        const c = { info: 0, success: 0, warning: 0, error: 0 };
        for (const e of events)
            c[e.level]++;
        return c;
    }, [events]);
    useEffect(() => {
        if (!ui.eventLogAutoScroll || collapsed)
            return;
        const el = listRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [visible, ui.eventLogAutoScroll, collapsed]);
    const onPick = useCallback((id) => {
        requestFocusNode(id);
        onNavigate?.();
    }, [requestFocusNode, onNavigate]);
    const toggleLevel = (l) => setFilters({
        eventLevels: filters.eventLevels.includes(l) ? filters.eventLevels.filter((x) => x !== l) : [...filters.eventLevels, l],
    });
    const HeightIcon = HEIGHT_ICON[height];
    return (<section className={cn("flex min-w-0 flex-col border-t border-border bg-bg-elevated transition-[height] duration-200", className)} style={{ height: collapsed ? 36 : HEIGHT_PX[height] }} aria-label="이벤트 로그">
      <div className="flex h-9 shrink-0 items-center gap-2 px-3">
        <button type="button" onClick={() => setUiState({ eventLogCollapsed: !collapsed })} aria-expanded={!collapsed} title={collapsed ? "이벤트 로그 펼치기" : "이벤트 로그 접기"} className="flex items-center gap-1 text-xs font-semibold text-fg hover:text-accent">
          {collapsed ? <ChevronUp size={14} aria-hidden/> : <ChevronDown size={14} aria-hidden/>}
          이벤트 로그
          <span className="tabular font-normal text-fg-subtle">
            {visible.length}
            {visible.length !== events.length ? `/${events.length}` : ""}
          </span>
        </button>

        <div className="ml-1 hidden items-center gap-1 sm:flex" role="group" aria-label="레벨 필터">
          {LEVELS.map((l) => (<Chip key={l} label={LEVEL_SHORT[l]} color={LEVEL_COLOR_VAR[l]} count={levelCounts[l]} active={filters.eventLevels.includes(l)} onClick={() => toggleLevel(l)} title={`${LEVEL_LABEL_KO[l]} 이벤트만 보기 (다중 선택, 없으면 전체)`}/>))}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <label className="flex cursor-pointer items-center gap-1 text-[11px] text-fg-muted" title="새 이벤트가 오면 맨 아래로 스크롤">
            <input type="checkbox" checked={ui.eventLogAutoScroll} onChange={(e) => setUiState({ eventLogAutoScroll: e.target.checked })} className="accent-accent"/>
            자동 스크롤
          </label>
          <IconButton label={`높이: ${HEIGHT_LABEL[height]} → ${HEIGHT_LABEL[NEXT_HEIGHT[height]]}`} onClick={() => {
            setHeight(NEXT_HEIGHT[height]);
            if (collapsed)
                setUiState({ eventLogCollapsed: false });
        }} className="size-7">
            <HeightIcon size={14}/>
          </IconButton>
          <IconButton label="이벤트 전체 지우기" onClick={clearEvents} disabled={events.length === 0} className="size-7">
            <Trash2 size={14}/>
          </IconButton>
        </div>
      </div>

      {!collapsed && (<ul ref={listRef} className="thin-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden border-t border-border/60 font-mono" aria-live="polite" aria-relevant="additions">
          {visible.length === 0 ? (<li className="px-3 py-3 text-[11px] text-fg-subtle">
              {events.length === 0 ? "아직 이벤트가 없습니다." : "선택한 레벨의 이벤트가 없습니다."}
            </li>) : (visible.map((e) => <EventRow key={e.id} e={e} nodeLabel={e.nodeId ? labelById.get(e.nodeId) : undefined} onPick={onPick}/>))}
        </ul>)}
    </section>);
});
